import React, { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, signOut,
  sendEmailVerification, sendPasswordResetEmail, updateProfile, getMultiFactorResolver,
  TotpMultiFactorGenerator, reauthenticateWithCredential, EmailAuthProvider, updatePassword,
} from "firebase/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fbAuth, googleProvider, firebaseErrorMessage } from "@/lib/firebase";

const AuthCtx = createContext(null);
const PENDING_KEY = "twin_pending_profile";

function readPending(email) {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    return p && p.email === email ? p : {};
  } catch { return {}; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const createSession = async (fbUser) => {
    const email = (fbUser.email || "").toLowerCase();
    const pending = readPending(email);
    const id_token = await fbUser.getIdToken(true);
    const { data } = await api.post("/auth/firebase/session", {
      id_token, name: pending.name || fbUser.displayName || undefined, company_name: pending.company_name || undefined,
    });
    localStorage.removeItem(PENDING_KEY);
    setUser(data);
    return { ok: true, user: data };
  };

  const handleSignIn = async (fn) => {
    try {
      const cred = await fn();
      if (cred.user.providerData.some((p) => p.providerId === "password") && !cred.user.emailVerified) {
        await signOut(fbAuth);
        return { ok: false, unverified: true, error: "Please verify your email first. Check your inbox for the link." };
      }
      return await createSession(cred.user);
    } catch (e) {
      if (e?.code === "auth/multi-factor-auth-required") {
        return { ok: false, mfaResolver: getMultiFactorResolver(fbAuth, e) };
      }
      if (e?.response) {
        await signOut(fbAuth).catch(() => {});
        return { ok: false, error: formatApiErrorDetail(e.response.data?.detail) };
      }
      return { ok: false, error: firebaseErrorMessage(e) };
    }
  };

  const login = (email, password) => handleSignIn(() => signInWithEmailAndPassword(fbAuth, email.trim(), password));
  const loginWithGoogle = () => handleSignIn(() => signInWithPopup(fbAuth, googleProvider));

  const completeMfa = (resolver, code) => handleSignIn(() => {
    const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) || resolver.hints[0];
    return resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim()));
  });

  const register = async ({ name, email, password, company_name }) => {
    try {
      const clean = email.trim().toLowerCase();
      const cred = await createUserWithEmailAndPassword(fbAuth, clean, password);
      if (name) await updateProfile(cred.user, { displayName: name });
      localStorage.setItem(PENDING_KEY, JSON.stringify({ email: clean, name, company_name }));
      await sendEmailVerification(cred.user);
      await signOut(fbAuth);
      return { ok: false, verificationSent: true, email: clean };
    } catch (e) {
      return { ok: false, error: firebaseErrorMessage(e) };
    }
  };

  const resendVerification = async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(fbAuth, email.trim(), password);
      await sendEmailVerification(cred.user);
      await signOut(fbAuth);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: firebaseErrorMessage(e) };
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(fbAuth, email.trim());
      return { ok: true };
    } catch (e) {
      if (e?.code === "auth/user-not-found") return { ok: true };
      return { ok: false, error: firebaseErrorMessage(e) };
    }
  };

  const changePassword = async (current, next) => {
    try {
      const fu = fbAuth.currentUser;
      if (!fu) return { ok: false, error: "For security, please sign out and sign in again, then retry." };
      await reauthenticateWithCredential(fu, EmailAuthProvider.credential(fu.email, current));
      await updatePassword(fu, next);
      return { ok: true };
    } catch (e) {
      if (["auth/invalid-credential", "auth/wrong-password"].includes(e?.code)) {
        return { ok: false, error: "Current password is incorrect." };
      }
      return { ok: false, error: firebaseErrorMessage(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    await signOut(fbAuth).catch(() => {});
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{
      user, booting, login, loginWithGoogle, completeMfa, register, resendVerification,
      resetPassword, changePassword, logout, setUser,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
